import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import styled from "styled-components";
import { Modal, Form, Input, Button, Alert, message } from "antd";
import { Link } from "react-router-dom";
import { EditOutlined } from "@ant-design/icons";

const DashboardStyle = styled.div`
  margin-left: 280px;
  overflow-x: hidden;
`;
const BASE_URL = process.env.REACT_APP_BASE_URL;

const ManagerAccount = () => {
  const [loading, setLoading] = useState(true);
  const [managerData, setManagerData] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [checkinsModalOpen, setCheckinsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [checkinsValue, setCheckinsValue] = useState("");

  const { token, email } = useAuth();

  const formatActiveSince = (dateString) => {
    const dateObj = new Date(dateString);
    const month = dateObj.toLocaleString("default", { month: "long" });
    const year = dateObj.getFullYear();
    return `${month} ${year}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const managerResponse = await axios.get(
          `${BASE_URL}/companies/manager/details`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const companyResponse = await axios.get(
          `${BASE_URL}/companies/details`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (
          managerResponse.data.status === "failed" &&
          managerResponse.data.message === "Manager not found for the company"
        ) {
          setManagerData(null);
        } else if (managerResponse.status === 200) {
          setManagerData(managerResponse.data);
        }

        if (companyResponse.status === 200) {
          setCompanyData({
            ...companyResponse.data,
            company_name: companyResponse.data.name,
            active_since: formatActiveSince(companyResponse.data.active_since),
            email: email,
            physical_address: companyResponse.data.physical_address,
            minimum_checkins: companyResponse.data.minimum_checkins,
            card_last4: companyResponse.data.last_4card,
          });
        } else {
          message.error("Error fetching company data");
        }
      } catch (error) {
        message.error("Error fetching data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, email]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
    if (managerData) {
      form.setFieldsValue({
        first_name: managerData.first_name,
        last_name: managerData.last_name,
        role: managerData.role,
        email: managerData.email,
      });
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    form.resetFields();
  };

  const handleCheckinsModalOpen = () => {
    setCheckinsModalOpen(true);
    setCheckinsValue(companyData?.minimum_checkins || "");
  };

  const handleCheckinsModalClose = () => {
    setCheckinsModalOpen(false);
  };

  const handleAddCard = async () => {
    try {
      const response = await axios.post(
        `${BASE_URL}/paystack/first-transaction`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.status && response.data.authorization_url) {
        window.open(response.data.authorization_url, "_blank");
      } else {
        message.error("Failed to initialize Paystack transaction");
      }
    } catch (error) {
      message.error("Error initializing Paystack transaction");
    }
  };

  const handleCheckinsSubmit = async () => {
    if (checkinsValue < 1) {
      message.error(
        "The number of check-ins must be greater than or equal to 1!"
      );
      return;
    }
    setLoading(true);

    try {
      const response = await axios.put(
        `${BASE_URL}/companies/minimum-checkins`,
        { minimum_checkins: checkinsValue },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.status === "success") {
        message.success("Minimum check-ins updated successfully");

        // Update companyData with the new minimum check-ins
        setCompanyData((prevData) => ({
          ...prevData,
          minimum_checkins: checkinsValue,
        }));
      } else {
        message.error(response.data.message);
      }
    } catch (error) {
      message.error("Error updating minimum check-ins");
    } finally {
      setLoading(false);
      handleCheckinsModalClose();
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);

    try {
      let response;
      if (managerData) {
        response = await axios.put(
          `${BASE_URL}/companies/manager/profile/update/${managerData.id}`,
          values,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } else {
        response = await axios.post(
          `${BASE_URL}/companies/manager/profile/create`,
          values,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }

      if (response.data.status === "success") {
        message.success(
          managerData
            ? "Manager profile updated successfully"
            : "Manager profile created successfully"
        );

        const managerResponse = await axios.get(
          `${BASE_URL}/companies/manager/details`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (managerResponse.status === 200) {
          setManagerData(managerResponse.data);
        } else {
          message.error("Error fetching manager data");
        }
      } else {
        message.error(response.data.message);
      }
    } catch (error) {
      message.error("Error submitting form");
    } finally {
      setLoading(false);
      handleCloseModal();
    }
  };

  if (loading) {
    return (
      <div className="text-center spinner">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <DashboardStyle>
      <div className="content-container">
        <br />
        <div className="col">
          <h3>Account</h3>
          <br />
        </div>
        <div>
          <main
            className="main-content border-radius-lg card container-fluid"
            style={{ padding: "23px" }}
          >
            <div className="row">
              <div className="col-4">
                <div>
                  <h5 style={{ color: "#5e95ff" }}>Company</h5>
                  <br />
                </div>
              </div>
              <div className="col-4" style={{ color: "black" }}>
                <b>
                  <h6>Name</h6>
                </b>
                <p>{companyData?.name || "N/A"}</p>
                <b>
                  <h6>Address</h6>
                </b>
                <p>{companyData?.physical_address || "N/A"}</p>
              </div>

              <div className="col-4" style={{ color: "black" }}>
                <b>
                  <h6>Active Since</h6>
                </b>
                <p>{companyData?.active_since || "N/A"}</p>

                <b>
                  <h6>Minimum No of Checkins</h6>
                </b>
                <p>
                  {companyData?.minimum_checkins || "N/A"}{" "}
                  <i
                    className="fa fa-pencil"
                    onClick={handleCheckinsModalOpen}
                    style={{
                      color: "grey",
                      cursor: "pointer",
                      fontSize: "18px",
                    }}
                    onMouseOver={(e) => {
                      e.target.style.color = "blue";
                    }}
                    onMouseOut={(e) => {
                      e.target.style.color = "grey";
                    }}
                  />
                </p>
              </div>
            </div>
            <br />
            <br />
            <div className="row">
              <div className="col-4">
                <div>
                  <h5 style={{ color: "#5e95ff" }}>Manager</h5>
                  <br />
                </div>
              </div>
              <div className="col-4" style={{ color: "black" }}>
                {managerData ? (
                  <>
                    <b>
                      <h6>Name</h6>
                    </b>
                    <p>
                      {managerData.first_name} {managerData.last_name}
                    </p>
                    <b>
                      <h6>Role</h6>
                    </b>
                    <p>{managerData.role}</p>
                  </>
                ) : (
                  <Button
                    className="btn btn-primary btn-sm"
                    style={{
                      backgroundColor: "#3c2f61",
                      color: "white",
                    }}
                    type="primary"
                    onClick={handleOpenModal}
                  >
                    Add Manager
                  </Button>
                )}
                <p>
                  <Link
                    to="#"
                    style={{
                      color: "blue",
                      textDecoration: "underline",
                      display: "inline-block",
                    }}
                    onClick={handleAddCard}
                  >
                    Add New Company Card
                  </Link>
                  <br></br>

                  {companyData?.card_last4 && (
                    <span style={{ color: "grey", fontSize: "15px" }}>
                      Card ending with ***{companyData?.card_last4}
                    </span>
                  )}
                </p>
              </div>
              <div className="col-4" style={{ color: "black" }}>
                <b>
                  <h6>Email</h6>
                </b>
                <p>{companyData?.email || "N/A"}</p>

                {managerData && (
                  <div>
                    <Link
                      to="#"
                      style={{
                        color: "blue",
                        textDecoration: "underline",
                        display: "inline-block",
                        marginLeft: "10px",
                        marginTop: "29px",
                      }}
                      onClick={handleOpenModal}
                    >
                      Update Manager
                    </Link>
                  </div>
                )}
              </div>
            </div>
            <br />
            <br />
            <div className="row">
              <div className="col-4">
                <div>
                  <h5 style={{ color: "#5e95ff" }}>Account</h5>
                  <br />
                </div>
              </div>
              <div className="col-4" style={{ color: "#3c2f61" }}>
                <b>
                  <h6>Change Password</h6>
                </b>
              </div>
              <div className="col-4" style={{ color: "#3c2f61" }}>
                <b>
                  <h6>Delete Account</h6>
                </b>
              </div>
            </div>
          </main>
        </div>
      </div>

      <Modal
        title={
          managerData ? "Update Manager Profile" : "Create Manager Profile"
        }
        visible={isModalOpen}
        onCancel={handleCloseModal}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="first_name"
            label="First Name"
            rules={[
              { required: true, message: "Please input the first name!" },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="last_name"
            label="Last Name"
            rules={[{ required: true, message: "Please input the last name!" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: "Please input the role!" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Please input the email!" },
              { type: "email", message: "Please enter a valid email!" },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item>
            <Button
              className="btn btn-primary btn-sm"
              style={{
                backgroundColor: "#3c2f61",
                color: "white",
              }}
              type="primary"
              htmlType="submit"
            >
              {managerData ? "Update" : "Create"}
            </Button>
            <Button
              className="btn btn-sm"
              style={{ marginLeft: "8px" }}
              onClick={handleCloseModal}
            >
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal for editing check-ins */}
      <Modal
        title="Edit Minimum Check-ins"
        visible={checkinsModalOpen}
        onCancel={handleCheckinsModalClose}
        footer={null}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Minimum Check-ins"
            rules={[
              {
                required: true,
                message: "Please enter the minimum check-ins!",
              },
              {
                type: "number",
                min: 1,
                message:
                  "The number of check-ins must be greater than or equal to 1!",
              },
            ]}
          >
            <Input
              type="number"
              value={checkinsValue}
              onChange={(e) => setCheckinsValue(e.target.value)}
            />
          </Form.Item>

          <Form.Item>
            <Button
              className="btn btn-primary btn-sm"
              style={{
                backgroundColor: "#3c2f61",
                color: "white",
              }}
              onClick={handleCheckinsSubmit}
            >
              Update
            </Button>
            <Button
              className="btn btn-sm"
              style={{ marginLeft: "8px" }}
              onClick={handleCheckinsModalClose}
            >
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </DashboardStyle>
  );
};

export default ManagerAccount;
